import { Observable, catchError, shareReplay, throwError } from "rxjs";
import { BaseService } from "src/Shared/Services/Base.service";
import { CompanyApiUrls } from "../Tools/CompanyApiUrls";
import {
  IImperansClinicOption,
  IImperansFranchiseOption,
} from "../Interfaces/IImperansReference";

export type ImperansReferenceKind = "franchises" | "clinics";

/**
 * The paired Imperans's franchise and clinic lists (Epic 6303, Feature CRM3), read through the CRM's
 * own proxy endpoints — the browser never talks to Imperans.
 *
 * Held for the session at most and never persisted (story 6374): the first read of a list for a
 * country is shared with every later reader in this tab, a failed read is forgotten so the next call
 * asks again, and a Retry asks fresh. A reload of the page starts from nothing, which is the point —
 * a deactivated franchise or clinic cannot linger in a stored copy.
 */
class ImperansReferenceService extends BaseService {
  private readonly cache = new Map<string, Observable<unknown>>();

  public getFranchises(country: string, fresh = false): Observable<IImperansFranchiseOption[]> {
    return this.cached("franchises", country, fresh, () =>
      this.http.get<IImperansFranchiseOption[]>(CompanyApiUrls.OnboardingReferenceFranchises(country)),
    );
  }

  public getClinics(country: string, fresh = false): Observable<IImperansClinicOption[]> {
    return this.cached("clinics", country, fresh, () =>
      this.http.get<IImperansClinicOption[]>(CompanyApiUrls.OnboardingReferenceClinics(country)),
    );
  }

  /** Drops every held list, so the next reader fetches again. */
  public forget(): void {
    this.cache.clear();
  }

  private cached<T>(
    kind: ImperansReferenceKind,
    country: string,
    fresh: boolean,
    fetch: () => Observable<T[]>,
  ): Observable<T[]> {
    const key = `${kind}:${`${country || ""}`.trim().toUpperCase()}`;
    if (fresh || !this.cache.has(key)) {
      const shared = fetch().pipe(
        // A failure is not cached: whoever asks next gets a real attempt (story 6374's Retry).
        catchError((error) => {
          this.cache.delete(key);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.cache.set(key, shared);
    }
    return this.cache.get(key) as Observable<T[]>;
  }
}

const imperansReferenceService = new ImperansReferenceService();
export default imperansReferenceService;
